import type { FixedAssetResources } from './en-US.js';

export const zhHant: FixedAssetResources = {
  nav: {
    myAssets: '我的資產',
    assets: '資產列表',
    locations: '存放地點',
    inventories: '盤點',
    import: '匯入',
  },
  common: {
    none: '—',
    unknown: '未知',
    all: '全部',
    save: '儲存',
    saved: '已儲存。',
    reload: '重新載入',
    edit: '編輯',
    add: '新增',
    active: '使用中',
    inactive: '已停用',
    deactivate: '停用',
    activate: '重新啟用',
  },
  field: {
    code: '資產編號',
    name: '名稱',
    type: '資產類型',
    accountTitle: '會計科目',
    assetClass: '資產分類',
    tangibility: '有形/無形',
    site: '據點',
    location: '存放地點',
    user: '使用人',
    manager: '資產管理員',
    status: '狀態',
    acquiredOn: '取得日期',
    currency: '貨幣',
    amount: '取得成本',
    amountJpy: '日圓金額',
    usefulLifeYears: '耐用年限',
    maker: '製造商',
    model: '型號',
    serialNumber: '序號',
    previousCode: '原資產編號',
    notes: '備註',
    disposedOn: '處置日期',
    disposalMethod: '處置方式',
    disposalNote: '處置說明',
  },
  myAssets: {
    title: '我的資產',
    empty: '沒有分配給您的資產。',
    inventoryBanner: '盤點「{{title}}」正在進行。請確認您手邊的資產。',
    confirm: '確認',
    confirmed: '已確認',
  },
  assets: {
    title: '資產列表',
    register: '登記資產',
    export: '匯出 CSV',
    search: '搜尋',
    searchPlaceholder: '資產編號、名稱、製造商、型號、序號',
    allSites: '所有據點',
    allTypes: '所有資產類型',
    openStatuses: '已處置以外',
    jpyMissing: '缺少日圓金額',
    userDisabled: '使用人已停用',
    empty: '沒有符合條件的資產。',
    printLabels: '列印標籤（{{selected}} 項）',
    clearSelection: '取消選取',
    selectPage: '全選本頁',
    selectionLimit: '最多可選取 {{max}} 項資產列印標籤。',
  },
  assetForm: {
    registerTitle: '登記資產',
    editTitle: '編輯資產',
    register: '登記',
    registerAnother: '以相同內容繼續登記',
    registered: '已登記 {{code}}。',
    jpyHint: '日本據點使用日圓以外的貨幣時必填。',
    siteNotConfigured: '尚未設定（據點的國家與貨幣由管理員設定）。',
    noUser: '無使用人',
    chooseUser: '選擇使用人',
  },
  asset: {
    details: '詳細資料',
    history: '歷史記錄',
    actions: '操作',
    changeUser: '變更使用人',
    changeManager: '變更資產管理員',
    changeLocation: '變更存放地點',
    changeSite: '移至其他據點',
    changeStatus: '變更狀態',
    dispose: '處置',
    revertDisposal: '撤銷處置',
    confirmRevert: '要撤銷 {{code}} 的處置嗎？資產將恢復到處置前的狀態。',
    conflict: '此資產已在其他地方被修改，請重新載入以查看最新狀態。',
    jpyMissing: '缺少日圓金額（日本據點必填）。',
    userDisabled: '使用人的帳戶已在 mai ID 中停用，請指定其他使用人。',
    noUser: '無使用人',
    noLocation: '無存放地點',
    hidden: '不顯示',
    disposalNoteHint: '選擇「其他」時必填。',
    attachments: '附件',
    printLabel: '列印標籤',
    confirmIn: '在「{{title}}」中確認',
    confirmedIn: '已在「{{title}}」中確認',
  },
  history: {
    empty: '尚無歷史記錄。',
    periods: '使用期間',
    period: '{{from}} – {{to}}',
    current: '至今',
    by: '{{name}}',
    system: '系統',
  },
  card: {
    title: '資產 {{code}}',
    returnHint: '如果您發現了此資產，請歸還給資產管理員。',
    open: '開啟資產',
  },
  attachments: {
    photos: '照片',
    documents: '文件',
    addPhoto: '新增照片',
    addDocument: '新增文件',
    hint: 'JPEG、PNG、WebP 或 PDF，最大 {{maxMiB}} MB。照片會在上傳前縮小。',
    full: '附件已達上限 {{max}} 個。',
    uploading: '正在上傳…',
    empty: '尚無附件。',
    remove: '刪除附件',
    removeNamed: '刪除 {{name}}',
    confirmRemove: '確定刪除 {{name}} 嗎？刪除後將不再顯示，但會作為記錄保留。',
  },
  labels: {
    title: '列印QR碼標籤',
    print: '列印',
    layout: '標籤紙',
    layoutA4: 'A4 標籤紙，24 張（70 × 37 mm）',
    layoutSingle: '標籤印表機（62 × 29 mm）',
    start: '起始位置',
    startHint: '跳過標籤紙上已使用的標籤（1 = 左上）。',
    summary: '共 {{labels}} 張標籤，{{sheets}} 頁。',
    printHint: '在列印對話方塊中請選擇 100% 縮放、無邊界。',
    missing: '找不到的資產編號：{{codes}}',
    noneSelected: '未選取資產。請在資產列表中選取。',
    toAssets: '前往資產列表',
  },
  inventories: {
    title: '盤點',
    start: '開始盤點',
    titleField: '標題',
    scope: '盤點的存放地點',
    wholeSite: '整個據點',
    startHint: '盤點對象為開始時該據點（或所選存放地點）的資產，之後登記的資產不包含在內。',
    empty: '尚無盤點。',
    startedAt: '開始時間',
    closedAt: '完成時間',
    progress: '已盤點 {{checked}} / {{total}}',
  },
  inventory: {
    close: '完成盤點',
    confirmClose: '確定完成「{{title}}」嗎？未盤點的資產將保持未盤點，資產資訊不會被變更。',
    export: '匯出結果（CSV）',
    expectedLocation: '登記存放地點',
    expectedUser: '登記使用人',
    result: '盤點結果',
    foundLocation: '實際存放地點',
    locationDiffers: '存放地點不符',
    onlyDiffers: '僅存放地點不符',
    selfConfirmed: '使用人自行確認',
    checked: '盤點人',
    note: '備註',
    check: '盤點',
    checkTitle: '盤點 {{code}}',
    found: '已確認',
    search: '資產編號或名稱',
    closedNotice:
      '此盤點已完成。如需將未找到的資產設為遺失，或移至實際存放地點，請在各資產頁面操作。',
  },
  import: {
    title: '從 CSV 匯入',
    intro:
      '請在試算表中填寫範本並儲存為 CSV。資產類型以前綴指定，據點以名稱指定，使用人和資產管理員以員工編號指定。標題列和選項可使用四種語言中的任一種。',
    template: '下載範本',
    chooseFile: '選擇 CSV 檔案',
    encoding: '字元編碼',
    encodings: {
      utf8: 'UTF-8',
      shiftJis: 'Shift_JIS（日文版 Excel）',
      gb18030: 'GB18030（簡體中文版 Excel）',
      big5: 'Big5（繁體中文版 Excel）',
    },
    createLocations: '建立尚不存在的存放地點',
    preview: '檢查檔案',
    valid: '未發現問題。將登記 {{rows}} 項資產。',
    invalid: '發現 {{issues}} 個問題。請修改檔案後重新檢查；存在問題時不會匯入任何內容。',
    missingLocations: '尚不存在的存放地點：{{names}}',
    commit: '匯入 {{rows}} 項資產',
    done: '已登記 {{rows}} 項資產。',
    locationsCreated: '已建立 {{created}} 個存放地點。',
    downloadCodes: '下載新資產編號（CSV）',
    row: '列',
    column: '欄位',
    problem: '問題',
    columns: {
      userEmployeeNumber: '使用人員工編號',
      managerEmployeeNumber: '資產管理員員工編號',
    },
    problems: {
      Required: '必填',
      Invalid: '值無效',
      TooLong: '過長',
      UnknownAssetType: '沒有此前綴或名稱的資產類型',
      InactiveAssetType: '此資產類型已停用',
      UnknownSite: '沒有此名稱的據點',
      SiteNotConfigured: '據點的國家和貨幣尚未設定',
      UnknownLocation: '該據點沒有此存放地點',
      InactiveLocation: '此存放地點已停用',
      UnknownEmployee: '沒有此員工編號的使用者',
      UserDisabled: '該使用者的帳號已停用',
      NotAssetManager: '不是資產管理員',
      StatusNotAllowed: '匯入時狀態只能為使用中、閒置或維修中',
      FutureDate: '日期在未來',
      InvalidCurrency: '貨幣代碼無效',
      InvalidAmount: '金額與貨幣不符',
      JpyAmountRequired: '日本據點必須填寫日圓金額',
      AssetNumberExhausted: '此資產類型的資產編號已用完',
      UnknownColumn: '未知的欄位',
      DuplicateColumn: '欄位重複',
      MissingColumn: '缺少必填欄位',
    },
  },
  locations: {
    title: '存放地點',
    add: '新增存放地點',
    empty: '此據點尚無存放地點。',
  },
  assetTypes: {
    title: '資產類型',
    description:
      '每種資產類型都有一個 1–4 個字母的前綴，作為資產編號的開頭（例如 PC0000000001）。發放資產編號後，前綴無法變更。',
    add: '新增資產類型',
    prefix: '前綴',
    prefixHint: '1–4 個字母 A–Z',
    prefixLocked: '已發放資產編號，前綴無法變更。',
    defaultAssetClass: '預設資產分類',
    defaultTangibility: '預設有形/無形',
    defaultUsefulLife: '預設耐用年限',
    empty: '尚無資產類型。',
  },
  sites: {
    title: '據點',
    description:
      'mai ID 的每個群組都是一個據點。設定國家與預設貨幣後，即可在該據點登記資產。日本據點也必須填寫日圓金額。',
    country: '國家',
    defaultCurrency: '預設貨幣',
    notConfigured: '未設定',
    jpyRequired: '需要日圓金額',
    set: '設定',
  },
  userPicker: {
    search: '姓名、電子郵件或員工編號',
  },
  assetManagers: {
    title: '資產管理員',
    description:
      '資產管理員可以登記和修改所有據點的資產與存放地點，並指定使用人。管理員隨時可以執行這些操作。',
    add: '新增資產管理員',
    name: '姓名',
    employeeNumber: '員工編號',
    site: '據點',
    grantedAt: '新增時間',
    managedAssets: '管理中的資產',
    remove: '移除',
    confirmRemove: '要將 {{name}} 從資產管理員中移除嗎？',
    empty: '尚未新增資產管理員。',
    unknownUser: '未知使用者',
    handOver: '移交資產',
    handOverTo: '接收的資產管理員',
    handedOver: '已移交 {{count}} 項資產。',
  },
  audit: {
    title: '稽核記錄',
    time: '時間',
    actor: '執行者',
    action: '操作',
    target: '對象',
    system: '系統',
    actions: {
      AssetTypeChanged: '變更資產類型',
      SiteSettingsChanged: '變更據點設定',
      StorageLocationChanged: '變更存放地點',
      AssetManagerGranted: '新增資產管理員',
      AssetManagerRevoked: '移除資產管理員',
      AssetDisposalReverted: '撤銷處置',
      AssetsImported: '匯入資產',
      AssetCsvExported: '匯出資產 CSV',
      InventoryStarted: '開始盤點',
      InventoryClosed: '完成盤點',
      LocalAdminGranted: '新增管理員',
      LocalAdminRevoked: '移除管理員',
    },
  },
};
